import { Descriptions, Segmented, Space, Table, Tag, Typography } from 'antd'
import { ColumnType } from 'antd/lib/table'
import { useState } from 'react'
import { centsToDollarsDisplay, percentageDisplay } from '../../displayFormatters'
import VSpace from '../../common/VSpace'
import Units from '../Units'
import { CustomizedValueDisplay } from '../CustomizableValue'
import { ItemWorkareaData, ViewItem } from '../itemModel'
import { hasValue } from '../../../helpers/nullable'
import { calculateUnitPrice } from '../../../domain/workareaItem'
import CalculationTooltip from './CalculationTooltip'
import {
  CalculationTotals,
  ItemCalculationMode,
  WorkareaCalculationGroup,
  formatDecimalFactor,
  groupCalculationsByWorkarea,
  overheadAmount,
  sumGrandTotals,
  taxAmount,
  unitProfit,
} from './itemCalculationsModel'

const { Text } = Typography

export type Props = {
  estimatedItems: ViewItem[]
  actualItems: ViewItem[]
  workareas: ItemWorkareaData[]
  groupSortIndexById?: Record<string, number>
}

const baseColumns: ColumnType<ViewItem>[] = [
  {
    title: 'Item',
    dataIndex: 'name',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'Quantity',
    align: 'right',
    render: (_, item) => <>{item.quantity} <Units units={item.units} maxWidth="80px" /></>,
  },
  {
    title: 'Unit Cost',
    align: 'right',
    render: (_, item) => <CustomizedValueDisplay isCustomized={item.hasCustomCost}>
      {centsToDollarsDisplay(item.cost)}
    </CustomizedValueDisplay>,
  },
  {
    title: 'Tax',
    align: 'right',
    render: (_, item) => {
      if (!item.applyTaxToBreakeven) {
        return <Text type="secondary">N/A</Text>
      }
      return <CalculationTooltip
        equation="Round(Cost * Tax Percentage)"
        values={`Round(${centsToDollarsDisplay(item.cost)} * ${formatDecimalFactor(item.taxPercentage)}) = ${centsToDollarsDisplay(taxAmount(item))}`}
      >
        {centsToDollarsDisplay(taxAmount(item))}{' '}
        <Text type="secondary">({percentageDisplay(item.taxPercentage * 100)})</Text>
      </CalculationTooltip>
    },
  },
  {
    title: 'Overhead',
    align: 'right',
    render: (_, item) => <CalculationTooltip
      equation="Round(Cost * Overhead Percentage)"
      values={`Round(${centsToDollarsDisplay(item.cost)} * ${formatDecimalFactor(item.overheadPercentage)}) = ${centsToDollarsDisplay(overheadAmount(item))}`}
    >
      {centsToDollarsDisplay(overheadAmount(item))}{' '}
      <Text type="secondary">({percentageDisplay(item.overheadPercentage * 100)})</Text>
    </CalculationTooltip>,
  },
  {
    title: 'Breakeven',
    align: 'right',
    render: (_, item) => <CalculationTooltip
      equation="Cost + Tax + Overhead"
      values={`${centsToDollarsDisplay(item.cost)} + ${centsToDollarsDisplay(taxAmount(item))} + ${centsToDollarsDisplay(overheadAmount(item))} = ${centsToDollarsDisplay(item.breakeven)}`}
    >
      {centsToDollarsDisplay(item.breakeven)}
    </CalculationTooltip>,
  },
  {
    title: 'Profit',
    align: 'right',
    render: (_, item) => <CalculationTooltip
      equation="Unit Price - Breakeven"
      values={`${centsToDollarsDisplay(item.unitPrice)} - ${centsToDollarsDisplay(item.breakeven)} = ${centsToDollarsDisplay(unitProfit(item))}`}
    >
      <CustomizedValueDisplay isCustomized={item.hasCustomProfitMargin}>
        {centsToDollarsDisplay(unitProfit(item))}{' '}
        <Text type="secondary">({percentageDisplay(item.profitMargin * 100)})</Text>
      </CustomizedValueDisplay>
    </CalculationTooltip>,
  },
  {
    title: 'Unit Price',
    align: 'right',
    render: (_, item) => {
      const override = item.hasCustomUnitPrice
        ? { label: 'Customized to', value: item.unitPrice }
        : hasValue(item.originalCatalogCustomPrice)
          ? { label: 'Catalog custom price of', value: item.unitPrice }
          : undefined
      const basePrice = calculateUnitPrice(item.breakeven, item.profitMargin)
      return <CalculationTooltip
        equation="Breakeven / (1 - Profit Margin)"
        values={`${centsToDollarsDisplay(item.breakeven)} / (1 - ${formatDecimalFactor(item.profitMargin)}) = ${centsToDollarsDisplay(basePrice)}`}
        customizedValue={override ? centsToDollarsDisplay(override.value) ?? undefined : undefined}
        customizedLabel={override?.label}
      >
        <CustomizedValueDisplay isCustomized={item.hasCustomUnitPrice}>
          {centsToDollarsDisplay(item.unitPrice)}
        </CustomizedValueDisplay>
      </CalculationTooltip>
    },
  },
  {
    title: 'Total Overhead',
    align: 'right',
    render: (_, item) => centsToDollarsDisplay(overheadAmount(item) * item.quantity),
  },
  {
    title: 'Total Breakeven',
    align: 'right',
    render: (_, item) => centsToDollarsDisplay(item.totalBreakeven),
  },
  {
    title: 'Total Price',
    align: 'right',
    render: (_, item) => <CustomizedValueDisplay isCustomized={item.hasCustomTotalPrice}>
      {centsToDollarsDisplay(item.totalPrice)}
    </CustomizedValueDisplay>,
  },
]

const summaryRow = (label: React.ReactNode, totals: CalculationTotals, bold: boolean, totalsColspan: number) => (
  <Table.Summary.Row style={bold ? { fontWeight: 'bold' } : undefined}>
    <Table.Summary.Cell index={0} colSpan={totalsColspan} align="right">{label}</Table.Summary.Cell>
    <Table.Summary.Cell index={totalsColspan} align="right">{centsToDollarsDisplay(totals.totalOverhead)}</Table.Summary.Cell>
    <Table.Summary.Cell index={totalsColspan + 1} align="right">{centsToDollarsDisplay(totals.totalBreakeven)}</Table.Summary.Cell>
    <Table.Summary.Cell index={totalsColspan + 2} align="right">{centsToDollarsDisplay(totals.totalPrice)}</Table.Summary.Cell>
  </Table.Summary.Row>
)

const WorkareaSection = ({ group, columns }: { group: WorkareaCalculationGroup, columns: ColumnType<ViewItem>[] }) => {
  const hasOccurrences = group.estimatedOccurrences > 1
  const totalsColspan = columns.length - 3
  return <VSpace size="small">
    <Space>
      <Text strong>{group.workareaName}</Text>
      {group.isOnHold && <Tag color="orange">On Hold</Tag>}
      {group.isApproved && <Tag color="green">Approved</Tag>}
      {hasOccurrences && <Text type="secondary">× {group.estimatedOccurrences} occurrences</Text>}
    </Space>
    <Table
      dataSource={group.items}
      rowKey="guid"
      columns={columns}
      size="small"
      pagination={false}
      summary={() => <>
        {summaryRow('Subtotal', group.subtotals, !hasOccurrences, totalsColspan)}
        {hasOccurrences && summaryRow(
          `× ${group.estimatedOccurrences} occurrences`,
          group.subtotalsWithOccurrences,
          true,
          totalsColspan
        )}
      </>}
    />
  </VSpace>
}

const ItemCalculationsContent = ({ estimatedItems, actualItems, workareas, groupSortIndexById }: Props) => {
  const [mode, setMode] = useState<ItemCalculationMode>('estimated')

  const items = mode === 'estimated' ? estimatedItems : actualItems
  const groups = groupCalculationsByWorkarea(items, workareas, mode, groupSortIndexById)
  const grandTotals = sumGrandTotals(groups)
  const columns = baseColumns

  return <VSpace size="large">
    <Segmented
      value={mode}
      onChange={value => setMode(value as ItemCalculationMode)}
      options={[
        { label: 'Estimated', value: 'estimated' },
        { label: 'Actual', value: 'actual' },
      ]}
    />

    {groups.length === 0
      ? <Table columns={columns} dataSource={[]} size="small" pagination={false} rowKey="guid" />
      : groups.map(group => <WorkareaSection key={group.workareaId} group={group} columns={columns} />)}

    <Descriptions
      title={<VSpace size={0}>
        <span>Job Totals</span>
        <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>
          On hold work areas are not included in these totals.
        </Text>
      </VSpace>}
      column={{ xs: 1, sm: 2, md: 3 }}
      bordered
      size="small"
    >
      <Descriptions.Item label="Total Cost">{centsToDollarsDisplay(grandTotals.totalCost)}</Descriptions.Item>
      <Descriptions.Item label="Total Tax">{centsToDollarsDisplay(grandTotals.totalTax)}</Descriptions.Item>
      <Descriptions.Item label="Total Overhead">{centsToDollarsDisplay(grandTotals.totalOverhead)}</Descriptions.Item>
      <Descriptions.Item label="Total Breakeven">{centsToDollarsDisplay(grandTotals.totalBreakeven)}</Descriptions.Item>
      <Descriptions.Item label="Total Price">{centsToDollarsDisplay(grandTotals.totalPrice)}</Descriptions.Item>
      <Descriptions.Item label="Total Profit">{centsToDollarsDisplay(grandTotals.totalProfit)}</Descriptions.Item>
    </Descriptions>
  </VSpace>
}

export default ItemCalculationsContent
